import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import template from './db-connection.hbs'
import FormView from 'scoreboard/components/form-view'
import MarkedField from 'scoreboard/components/marked-field'
import formHelper from 'scoreboard/components/form-helper'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import i18n from 'scoreboard/components/i18n'
import dropdown from 'scoreboard/components/dropdown'
import Connection from 'scoreboard/admin/connections/connection'
import DbConnectionWizard from 'scoreboard/admin/connections/db-connection-wizard'
import permissions from 'scoreboard/components/permissions'
import Importable from 'scoreboard/imports/model/importable'
import DataSourceSavingView from './data-source-saving-view'

/**
 * Second step of the values import wizard for database connections
 * that will to edit the database connection info (name, connection, and sql query)
 */
const DbConnection = DataSourceSavingView.extend({

	tagName: 'form',

	className: 'vi-wizard vi-db-connection',

	events: {
		'click .add-database': 'add',
		'click .cancel': 'close'
	},

	/**
	 * Form for editing the database connection info (name, connection, and sql
	 * query). Usually part of a wizard. Used when creating datasets, value imports
	 * and item imports (and possibly other places as well)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 * @param {Import|Dataset} options.model
	 * @param {Array} options.connections array of connections the user has
	 * access to
	 * @param {Boolean} [options.dataSourceTypeChosen] if the user has already
	 * selected the dataSource type (ex via a wizard that is using
	 * data-source-type-chooser.js)
	 * @param {Object} [options.labels] label information for building title
	 */
	initialize: function(options) {
		this.options = options || {}
		this.connections = this.options.connections || []
		this.currentConnection = this.options.currentConnection
		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: function() {
				return _.isNil(this.formView.test())
			}.bind(this)
		})
		// listen for newly added connection
		Backbone.on('add:import-connection', this._onAdd.bind(this))
		// check to see if the data source type has already been selected
		// using the new flow for choosing import data source
		this.dataSourceTypeChosen = _.get(this.options, 'dataSourceTypeChosen', false)
		this.selectedDbProduct = this.getSelectedDbProduct()
		this.updateAvailableConnections()
	},

	/**
	 * The database object inside [DataSourceMetadata.kt]
	 * @returns {connectionId, query}
	 */
	_databaseMetadata() {
		const metadata = this.model.get('dataSourceMetadata')
		if (_.isNil(metadata.database)) {
			metadata.database = {}
		}
		// #48286 remove any null or undefined properties so
		// formWriter.writeToDom doesn't incorrectly select an empty option
		return _.omitBy(metadata.database, _.isNil)
	},

	render: function() {
		this.$el.empty().append(template(this.context()))

		this.options.wizard.render({
			formView: this.formView,
			stateSaver: this.saveState.bind(this)
		})

		this.sqlField = new MarkedField({
			el: this.$('[name=query]'),
			validator: requiredValidator
		}).render()

		this.renderConnections()

		// #48217 prevent submission if no db connection selected (this can
		// happen if there are no available db connections for the user)
		this.connectionSelect = new MarkedField({
			el: this.$('[name=connectionId]'),
			validator: requiredValidator
		}).render()

		this.secure()

		this.toggleSqlPanelDisplay()
		this.renderBanner()

		formHelper.writeToDom(this._databaseMetadata(), this.el)
		this.formView.start()
		// #31642 display message if database is offline/unavailable
		let messageCode = this.model.getDataSourceMetadata('messageCode')
		if (messageCode) {
			let label = _.get(this.model.attributes, 'connection.label')
			this.formView.errorMessage(i18n.get(messageCode, label))
		}
		return this
	},

	secure: function() {
		if (!permissions.modifyImportConnections()) {
			// remove the "New Connection" link
			this.$('.add-database').remove()
			// if there are no connections the banner will have an explanation,
			// but hide it otherwise
			if (!this.noConnections()) {
				this.$('.banner.instructions').addClass('is-not-displayed')
			}
		}
	},

	context: function() {
		let title = this.options.title ||i18n.get('admin.importConnections.database.singular')
		// #44888 title for importables using new datasource selection flow
		if (this.model instanceof Importable) {
			let titleKey, typeKey
			if (this.model.get('type') === 'dataset') {
				typeKey = 'datasets.dataset'
				titleKey = this.model.isNew() ?
					'admin.import.datasource.from.database.create' :
					'admin.import.datasource.type.edit'
			} else {
				// value imports
				if (this.model.isNew()) {
					titleKey = 'admin.import.datasource.from.database'
					typeKey = _.get(this.options, 'labels.plural', 'admin.import')
				} else {
					titleKey = 'admin.import.datasource.type.edit'
					typeKey = _.get(this.options, 'labels.singular', 'admin.import')
				}
			}
			title = i18n.get(titleKey, typeKey)
		}

		return { title }
	},

	/**
	 * If the db product has already been selected
	 */
	filterByDbProduct: function() {
		return this.dataSourceTypeChosen && !_.isNil(this.selectedDbProduct)
	},

	/**
	 * Returns the previously selected db product, if available
	 * This returns the db product type selected in the
	 * data-source-type-chooser.js step of the wizard
	 */
	getSelectedDbProduct: function() {
		if (this.filterByDbProduct) {
			return _.get(this.options, 'dataSourceTypeObj.type')
		}
	},

	renderBanner: function() {
		let msg
		let msgCode = 'admin.import.databaseConnection.instructions'
		// if there are no available connections, and the user can't add a new
		// connection, modify the instructions
		if (this.noConnections() && !permissions.modifyImportConnections()) {
			msgCode = 'admin.import.connections.none'
		}
		// if we are only showing connections for a selected db product,
		// build a message using that label
		if (this.filterByDbProduct()) {
			let dbKey = 'admin.databaseConnections.type.' + this.selectedDbProduct
			msgCode = msgCode + '.withArg'
			msg = i18n.get(msgCode, dbKey)
		} else {
			msg = i18n.get(msgCode)
		}
		this.$('.banner.instructions').empty().text(msg)
	},

	noConnections: function() {
		return _.isEmpty(this.availableConnections)
	},

	renderConnections: function() {
		this.$('[name=connectionId]').empty()
		dropdown.idLabels(this.$('[name=connectionId]'), this.availableConnections)
	},

	/**
	 * Returns available db connections for the user
	 * This will be filtered by the db product if selected in a previous wizard step
	 */
	updateAvailableConnections: function() {
		let connections = _.cloneDeep(this.connections)
		// #53861 filter connections for type, status and product
		connections = _(connections)
			.filter(connection => {
				return connection.type === 'database' && connection.status === 'online'
			})
			.filter(connection => {
				// #44888 if this is a wizard using the new flow for selecting a
				// datasource type, filter the connections by the selected db product
				if (this.filterByDbProduct() && !_.isNil(connection.product)) {
					return connection.product === this.selectedDbProduct
				}
				return true
			}).value()
		// if the user isn't an owner of the existing connection, it won't be
		// passed into this.options.connections. That's fine, they have access
		// to the dataset so just for the purposes of this dataset they can also see
		// the connection so go ahead and add it
		// #53861 add this existing connection (if needed) *after* filtering any
		// existing connections
		const {id, label, type} = this.currentConnection || {}
		if (id && !_.find(connections, c => c.id == id)) { // need type coercion here (don't use {id: id})
			connections.push({
				id,
				label,
				type
			})
		}
		// then sort by label
		connections = _(connections)
			.sortBy(connection => connection.label.toLocaleLowerCase())
			.value()
		this.availableConnections = connections
	},

	toggleSqlPanelDisplay: function() {
		// #48217 hide sql query panel if there are no connections available
		this.$('.sql-query').toggleClass('is-not-displayed', this.noConnections())
	},

	// when the user clicks on new connection
	add: function() {
		let product
		// #44888 if this is a wizard using the new flow for selecting a datasource
		// type, open the new Connection wizard with the product already selected
		if (this.filterByDbProduct()) {
			product = this.selectedDbProduct
		}
		let database = new Connection({ standard: true, type: 'database', product})

		new DbConnectionWizard({
			model: database,
			validationUrl: 'api/admin/import-connections/validate'
		}).start()
	},

	// callback after a new DB connection has been added
	_onAdd: function(connection) {
		this.connections.push(connection)
		this.updateAvailableConnections()
		this.renderConnections()
		this.$('[name=connectionId]').val(connection.id).change()
		this.toggleSqlPanelDisplay()
	},

	saveState: function() {
		const database = formHelper.readFromDom(this.el)
		const selectedConnection = _.find(this.options.connections,
				c => c.id == database.connectionId)
		database.label = _.get(selectedConnection, 'label')
		database.product = _.get(selectedConnection, 'product')
		this.model.get('dataSourceMetadata').database = database
	},

	close: function() {
		_.invoke(this.xhr, 'abort')
	},

	submit: function() {
		this.saveState()

		return this.xhr = $.ajax({
			method: 'post',
			data: JSON.stringify(this.model.attributes),
			url: _.result(this.model, 'getValidateDbConnectionUrl'),
			success: this.success.bind(this),
			error: (xhr) => {
				// #51058 use this.formView.error so we have a specific error
				// message in the banner than can be clicked to view stacktrace
				this.formView.error(xhr)
				// the user should be able to press Next again because they
				// could make a change on the server that would make the values
				// on the form become valid to submit again
				this.formView.primaryButton.enable()
			}
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView, this.sqlField, this.connectionSelect], 'remove')
	}

})

export default DbConnection
DbConnection.__filename__ = 'imports/db-connection.js'
